import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Users, Building2, FileText } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

// Import the tab components (we'll create these next)
import LobbyistsTab from '../components/lobbying/LobbyistsTab';
import ClientsTab from '../components/lobbying/ClientsTab';
import BillsTab from '../components/lobbying/BillsTab';
import Tabs from '@shared/ui/Tabs';

type TabId = 'lobbyists' | 'clients' | 'bills';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  component: React.ComponentType;
}

const tabs: Tab[] = [
  { id: 'lobbyists', label: 'Lobbyists', icon: Users, component: LobbyistsTab },
  { id: 'clients', label: 'Clients', icon: Building2, component: ClientsTab },
  { id: 'bills', label: 'Bills', icon: FileText, component: BillsTab },
];

const LobbyingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('lobbyists');
  const [mountedTabs, setMountedTabs] = useState<Set<TabId>>(() => new Set(['lobbyists']));
  
  // Set page title in header
  usePageTitle('Lobbying', 'Browse lobbyists, clients, and lobbying filings');

  // Handle URL hash fragments and legacy ?tab= query for tab switching
  useEffect(() => {
    const hash = location.hash.replace('#', '') as TabId;
    const tabParam = new URLSearchParams(location.search).get('tab') as TabId | null;
    const resolvedTab =
      hash && tabs.some((tab) => tab.id === hash)
        ? hash
        : tabParam && tabs.some((tab) => tab.id === tabParam)
          ? tabParam
          : null;

    if (resolvedTab) {
      setActiveTab(resolvedTab);
      if (!hash && tabParam) {
        const search = location.search || '';
        navigate(
          { pathname: location.pathname, search, hash: `#${resolvedTab}` },
          { replace: true }
        );
      }
    } else if (!hash) {
      setActiveTab('lobbyists');
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  return (
    <div className="space-y-6 pt-2">

      {/* Header with Tabs Only - Main title comes from Layout */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-4">
        {/* Tab Navigation — the hash stays the source of truth for linkability */}
        <div className="min-w-0 flex-1">
          <Tabs
            level="section"
            ariaLabel="Lobbying sections"
            tabs={tabs.map((tab) => ({ id: tab.id, label: tab.label, icon: tab.icon }))}
            activeId={activeTab}
            onChange={(id) => {
              window.location.hash = id;
              setActiveTab(id as TabId);
            }}
          />
        </div>

      </div>

      {/* Tab Content — keep mounted to avoid reload flash when switching tabs */}
      <div className="flex-1">
        {tabs.map((tab) => {
          if (!mountedTabs.has(tab.id)) {
            return null;
          }
          const TabComponent = tab.component;
          return (
            <div key={tab.id} className={activeTab === tab.id ? undefined : 'hidden'}>
              <TabComponent />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LobbyingPage;
